import { useState } from 'react';
import { Button, DateRangePicker, Popover } from '@servicecompressionllc/sc-design-system';
import { Calendar } from '@servicecompressionllc/sc-design-system/icons';
import { formatDayKey } from '../../lib/format.js';
import { USAGE_PERIODS } from '../../lib/types.js';
import type { UsagePeriod } from './useUsageLogic.js';

const DAY_MS = 86_400_000;

/** A local "YYYY-MM-DD" for the viewer's today. */
const todayKey = (now: Date): string =>
  `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;

/** Why a custom range can't be applied, or null when it can. Mirrors the API's rules. */
export const rangeProblem = (from: string, to: string, now: Date = new Date()): string | null => {
  if (!from || !to) return 'Choose both days.';
  if (from > to) return 'The first day comes after the last.';
  const today = todayKey(now);
  if (from > today) return 'That range starts in the future.';
  if (daysBetween(from, today) > 366) return 'Ranges go up to 1 year back.';
  return null;
};

/** The earliest day a range may start: a year back, today included (as the API allows). */
const yearBack = (today: string): string =>
  new Date(Date.parse(`${today}T00:00:00Z`) - 365 * DAY_MS).toISOString().slice(0, 10);

interface PeriodPickerProps {
  period: UsagePeriod;
  onChange: (p: UsagePeriod) => void;
}

export const PeriodPicker = ({ period, onChange }: PeriodPickerProps) => {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(period.kind === 'custom' ? period.from : '');
  const [to, setTo] = useState(period.kind === 'custom' ? period.to : '');
  const problem = rangeProblem(from, to);
  const custom = period.kind === 'custom';

  const apply = () => {
    if (problem) return;
    onChange({ kind: 'custom', from, to });
    setOpen(false);
  };

  return (
    <div role="group" aria-label="Period" className="flex flex-wrap gap-1">
      {USAGE_PERIODS.map((p) => (
        <Button
          key={p}
          size="sm"
          variant={period.kind === 'preset' && period.period === p ? 'primary' : 'secondary'}
          aria-pressed={period.kind === 'preset' && period.period === p}
          onClick={() => onChange({ kind: 'preset', period: p })}
        >
          {p}
        </Button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <Button size="sm" variant={custom ? 'primary' : 'secondary'} aria-pressed={custom}>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} aria-hidden="true" />
              {custom
                ? `${formatDayKey(period.from)} to ${formatDayKey(period.to)}`
                : 'Custom range'}
            </span>
          </Button>
        </Popover.Trigger>
        <Popover.Content
          align="end"
          aria-label="Choose a date range"
          className="w-[26.25rem] max-w-[90vw]"
        >
          <div className="p-4">
            <DateRangePicker
              value={{ from: from || undefined, to: to || undefined }}
              onChange={(r) => {
                setFrom(r.from ?? '');
                setTo(r.to ?? '');
              }}
              min={yearBack(todayKey(new Date()))}
              max={todayKey(new Date())}
            />
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-outline bg-surface px-4 py-3">
            <span
              className="text-xs text-ink-subtle"
              role={problem && from && to ? 'alert' : undefined}
            >
              {problem && from && to
                ? problem
                : `${from && to && !problem ? `${String(daysBetween(from, to))} days · ` : ''}up to 1 year back`}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={apply} disabled={problem !== null}>
                Apply
              </Button>
            </div>
          </div>
        </Popover.Content>
      </Popover>
    </div>
  );
};
